import { useEffect, useState } from 'react';
import type { UploadItem } from '../types';

interface Props {
  items: UploadItem[];
  onClose: () => void;
  bottomOffset?: number;
}

export default function UploadToast({ items, onClose, bottomOffset = 24 }: Props) {
  const [minimized, setMinimized] = useState(false);

  const uploading  = items.filter(i => i.status === 'uploading');
  const completed  = items.filter(i => i.status === 'completed');
  const errors     = items.filter(i => i.status === 'error');
  const allDone    = items.length > 0 && uploading.length === 0;

  // Auto-close 4s after all done with no errors
  useEffect(() => {
    if (allDone && errors.length === 0) {
      const t = setTimeout(onClose, 4000);
      return () => clearTimeout(t);
    }
  }, [allDone, errors.length, onClose]);

  if (items.length === 0) return null;

  const headerText = uploading.length > 0
    ? `Subiendo ${uploading.length} archivo${uploading.length > 1 ? 's' : ''}…`
    : errors.length > 0
    ? `${completed.length} completado(s) · ${errors.length} error(es)`
    : `${completed.length} archivo${completed.length > 1 ? 's' : ''} subido${completed.length > 1 ? 's' : ''}`;

  return (
    <div
      className="feebbo-toast rounded-3 shadow-lg"
      style={{ 
        position: 'fixed', 
        bottom: bottomOffset, 
        right: 24, 
        width: 340, 
        zIndex: 1070,
        transition: 'bottom 0.3s ease'
      }}
    >
      {/* Header */}
      <div
        className="card-header d-flex align-items-center justify-content-between py-2 px-3"
        style={{ cursor: 'pointer', borderBottom: minimized ? 'none' : `1px solid var(--feebbo-border)` }}
        onClick={() => setMinimized(m => !m)}
      >
        <div className="d-flex align-items-center gap-2">
          {uploading.length > 0 ? (
            <div className="spinner-border spinner-border-sm" style={{ width: 16, height: 16, color: 'var(--feebbo-accent)' }} />
          ) : errors.length > 0 ? (
            <i className="bi bi-exclamation-triangle-fill text-warning" />
          ) : (
            <i className="bi bi-check-circle-fill text-success" />
          )}
          <span className="fw-semibold small">{headerText}</span>
        </div>
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-link p-0 text-body-secondary"
            onClick={e => { e.stopPropagation(); setMinimized(m => !m); }}
            title={minimized ? 'Expandir' : 'Minimizar'}
          >
            <i className={`bi bi-chevron-${minimized ? 'up' : 'down'}`} />
          </button>
          <button
            className="btn btn-sm btn-link p-0 text-body-secondary ms-1"
            onClick={e => { e.stopPropagation(); onClose(); }}
            title="Cerrar"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      </div>

      {/* File list */}
      {!minimized && (
        <div style={{ maxHeight: 280, overflowY: 'auto', scrollbarWidth: 'thin' }}>
          {/* Ordenar: uploading primero, error después, completed al final */}
          {[...items].sort((a, b) => {
            const priority = { uploading: 0, error: 1, completed: 2 };
            return priority[a.status] - priority[b.status];
          }).map(item => (
            <div key={item.id} className="px-3 py-2" style={{ borderBottom: '1px solid var(--feebbo-border)' }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                {item.status === 'uploading'  && <i className="bi bi-file-earmark text-primary" />}
                {item.status === 'completed'  && <i className="bi bi-file-earmark-check text-success" />}
                {item.status === 'error'      && <i className="bi bi-file-earmark-x text-danger" />}

                <span
                  className="small text-truncate flex-grow-1 fw-medium"
                  title={item.name}
                  style={{ maxWidth: 220 }}
                >
                  {item.name}
                </span>

                <span className="small text-body-secondary ms-auto" style={{ flexShrink: 0 }}>
                  {item.status === 'uploading'  && `${item.progress}%`}
                  {item.status === 'completed'  && <span className="text-success">Listo</span>}
                  {item.status === 'error'      && <span className="text-danger">Error</span>}
                </span>
              </div>

              {item.status === 'uploading' && (
                <div className="progress" style={{ height: 3, background: 'var(--feebbo-surface)' }}>
                  <div
                    className="feebbo-progress-bar progress-bar"
                    style={{ width: `${item.progress}%`, transition: 'width 0.2s ease' }}
                  />
                </div>
              )}

              {item.status === 'error' && item.error && (
                <p className="text-danger mb-0" style={{ fontSize: 11 }}>{item.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
